<template>
	<div class="product-modal">
		<!-- Grid area image -->
		<div class="image">
			<ECommerceProductImageCarousel
				:product-prop="rawProduct"
				:modal="true"
				class="carousel"
			/>
		</div>

		<!-- Grid area content -->
		<div class="content">
			<div class="title">
				<TnBadge
					:size="screenSize < 768 ? 's' : 'm'"
					class="badge"
					v-if="!!rawProductRef.badgeText"
				>
					{{ rawProductRef.badgeText }}
				</TnBadge>
				<TnParagraph
					size="m"
					responsive
					class="black"
				>
					{{ product.brand }}
				</TnParagraph>
				<TnHeading
					type="h3"
					size="s"
					responsive
					bold
				>
					{{ productNameWithoutBrand }}
				</TnHeading>
			</div>

			<div class="price">
				<TnHeading
					responsive
					bold
					size="l"
					class="price-top"
					:key="formattedPrice"
				>
					{{ formattedSalePrice ? formattedSalePrice : formattedPrice }}
				</TnHeading>
				<TnParagraph
					v-if="formattedSalePrice"
					responsive
					size="l"
					class="price-bottom"
					:class="{ 'strike-through': formattedSalePrice }"
					:key="formattedSalePrice"
				>
					Før {{ formattedPrice }}
				</TnParagraph>
			</div>

			<ECommerceProductColorPicker
				class=""
				:product-prop="product"
				@change="colorChanged($event, product)"
				handle-color-change-manually
			/>

			<TnButton
				class="add-button"
				@click.stop="addToCart(rawProduct)"
				:size="screenSize < 768 ? 'xs' : 's'"
			>
				Legg i handlekurv
			</TnButton>
		</div>

		<!-- 
				Commented out rating component till ratings are back up and running correctly
			-->
		<!-- <div v-if="averageRating > 0">
				<TnRating
					:rating="averageRating"
					size="l"
					class="margin-bottom-l hidden-mobile"
					:class="{ 'no-rating': averageRating === 0 }"
					v-if="false"
				/>
			</div> -->

		<!-- Grid area description -->
		<div class="description">
			<TnDivider
				class="divider hidden-mobile"
				size="m"
				:width="'full'"
			/>
			<TnParagraph
				size="m"
				class="hidden-mobile margin-bottom-s black"
				bold
			>
				Om {{ product.productName }}
			</TnParagraph>
			<RichText
				class="description"
				v-if="description"
				:text="description"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useProductNameFormatter, useProductPriceFormatter, useAddToCartFormatter } from "./ECommerceProductFormatters";
import type { RawProduct, Product, Variant } from "./types";

// Props
const props = defineProps<{
	product: Product;
	rawProduct: RawProduct;
}>();

// Emits
const emit = defineEmits(["color-changed", "add-to-cart", "close-modal"]);

// Methods
const colorChanged = (variant: Variant, product: Product) => {
	emit("color-changed", product, variant.id);
};

const addToCart = (rawProduct: RawProduct) => {
	emit("add-to-cart", useAddToCartFormatter(rawProduct));
};

const handleKeyDown = (event: KeyboardEvent) => {
	if (event.key === "Escape") {
		emit("close-modal");
	}
};

// Computed
// const averageRating = computed(() => {
//   return props.rawProduct.rating || 0;
// });

const { productNameWithoutBrand } = useProductNameFormatter(props.product);

const description = computed(() => {
	return props.product?.productDescription;
});

// Reactive variables
watch(
	() => props.rawProduct,
	(newRawProduct) => {
		rawProductRef.value = newRawProduct;
	},
);
const rawProductRef = ref(props.rawProduct);

const { formattedPrice, formattedSalePrice } = useProductPriceFormatter(rawProductRef);

const screenSize = ref(window.innerWidth);

// Lifecycle hooks
onMounted(() => {
	window.addEventListener("keydown", handleKeyDown);
});
onUnmounted(() => {
	window.removeEventListener("keydown", handleKeyDown);
});
</script>

<style lang="scss" scoped>
.product-modal {
	display: grid;
	width: 767px;
	height: fit-content;
	grid-template-columns: 1fr 1fr;
	grid-template-areas:
		"image content"
		"description content";
	@media (max-width: 852px) {
		max-width: 100%;
		grid-template-columns: 1fr;
		gap: $spacing-l;
		grid-template-areas:
			"image"
			"content"
			"description";
	}
}
.image {
	min-width: 100%;
	max-width: 100%;
	width: 100%;
	grid-area: image;
	height: 325px;
	@include breakpoint(mobile) {
		height: 225px;
	}
	.carousel {
		padding: 0 !important;
	}
	:deep() {
		.carousel {
			height: 100%;
		}
	}
}
.content {
	grid-area: content;
	display: flex;
	flex-direction: column;
	gap: $spacing-xl;
	@include breakpoint(mobile) {
		gap: $spacing-l;
	}
}
.badge {
	margin-bottom: $spacing-m;
	@include breakpoint(mobile) {
		margin-bottom: $spacing-s;
	}
}
.image,
.description {
	padding-right: $spacing-l;
	@include breakpoint(mobile) {
		padding-right: 0;
	}
}
.description {
	grid-area: description;
	& {
		.divider {
			padding-right: $spacing-l;
			margin: $spacing-xl 0;
		}
	}
}
.hidden-mobile {
	@include breakpoint(mobile) {
		display: none;
	}
}
.price {
	display: flex;
	flex-direction: column;
	justify-content: center;
	& {
		&-top {
			color: $color-primary-mid;
		}
		&-bottom {
			line-height: inherit !important;
		}
	}
	.strike-through {
		text-decoration: line-through;
		margin-right: $spacing-s;
	}
}
.black {
	color: black;
}
.add-button {
	@include breakpoint(mobile) {
		height: 32px;
	}
}
</style>
